import { useEffect, useRef, useState } from "react";
import { Crosshair, MapPin, Search, Loader2 } from "lucide-react";
import { PLACES } from "@/constants/places";
import { api } from "@/lib/api";
import { labelFor } from "@/lib/useRoute";

const useSearch = (q) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 3) {
      setResults([]);
      return undefined;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/geo/search", { params: { q: term } }).then((r) => setResults(r.data)).catch(() => setResults([])).finally(() => setLoading(false));
    }, 450);
    return () => {
      clearTimeout(t);
    };
  }, [q]);
  return { results, loading };
};

export const PlaceSearch = ({ label, value, onChange, active, onActivate, color, testId, allowMyLocation }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const { results, loading } = useSearch(q);
  const box = useRef(null);
  const term = q.trim().toLowerCase();
  const landmarks = PLACES.filter((p) => !term || p.name.toLowerCase().includes(term)).slice(0, term ? 4 : 16);

  useEffect(() => {
    const close = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => {
      document.removeEventListener("mousedown", close);
    };
  }, []);

  const choose = (p) => {
    onChange({ lat: p.lat, lng: p.lng, address: p.name });
    setQ("");
    setOpen(false);
  };
  const myLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const { latitude: lat, longitude: lng } = p.coords;
        onChange({ lat, lng, address: await labelFor(lat, lng) });
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  return (
    <div ref={box} onClick={onActivate} className={`relative rounded-2xl border-2 p-4 transition-colors duration-200 ${active ? "border-blue-600 bg-blue-50/50" : "border-slate-100 bg-white"}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500"><span className={`h-2.5 w-2.5 rounded-full ${color}`} /> {label}</span>
        <div className="flex gap-1.5">
          {allowMyLocation && (
            <button type="button" data-testid={`${testId}-my-location-button`} onClick={myLocation} className="flex items-center gap-1 rounded-full bg-blue-700 px-2.5 py-0.5 text-[11px] font-semibold text-white">
              {locating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Crosshair className="h-3 w-3" />} My location
            </button>
          )}
          <button type="button" data-testid={`${testId}-map-mode-button`} onClick={onActivate} className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}>
            {active ? "Tap the map" : "Pin on map"}
          </button>
        </div>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          data-testid={`${testId}-search-input`}
          value={open ? q : value?.address || ""}
          onFocus={() => setOpen(true)}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a place, street or landmark"
          className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-blue-600"
        />
      </div>
      {open && (
        <div data-testid={`${testId}-suggestions`} className="absolute left-4 right-4 z-[1000] mt-1 max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xl">
          {loading && <p className="px-3 py-2 text-xs text-slate-400">Searching Port Moresby…</p>}
          {results.map((r, i) => (
            <button type="button" key={`r${i}`} data-testid={`${testId}-result-${i}`} onClick={() => choose(r)} className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" /><span className="truncate">{r.name}</span>
            </button>
          ))}
          <p className="bg-slate-50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Popular landmarks</p>
          {landmarks.map((p) => (
            <button type="button" key={p.name} data-testid={`${testId}-option-${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => choose(p)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50">
              <MapPin className="h-4 w-4 shrink-0 text-amber-500" />{p.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
